import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { ActivityAsyncAdapter } from './activity.adapter';
import { Metamodel } from 'app/model/base/model';
import _ from 'lodash';
import { IFabricApprovalActivity, stringIsNullOrEmpty, ReferenceAdapter, ModelConstants, Office365EmailLinkedService, PostInType, MicrosoftTeamsLinkedService, ApprovalType, FabricApprovalActivity, OperationType } from 'shell-models-lib';
import { ValueAdapter } from '../value.adapter';
import { linkedServiceReferenceWithoutParameterizationRemainer } from '../reference-remainer';
import { getFabricLsForParameterizedConnections } from '../utils';
import { WebActivityLinkedService } from 'app/model/linkedservice/web-activity.linkedservice';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(FabricApprovalActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            approvers: '*',
            description: '*',
            approvalRequestTitle: '*',
            body: '*',
            operationType: '*',
            chatId: '*',
            teamId: '*',
            channelId: '*',
            upn: '*'
        },
        linkedServiceName: linkedServiceReferenceWithoutParameterizationRemainer,
        externalReferences: '*'
    }
})
export class FabricApprovalActivityAdapter extends ActivityAsyncAdapter<FabricApprovalActivity, IFabricApprovalActivity> {

    protected async _toResource(activity: FabricApprovalActivity): Promise<IFabricApprovalActivity> {
        const modelApprovalActivity = super._getCommonActivityProperties(
            activity, Metamodel.modelFor(FabricApprovalActivity).name) as IFabricApprovalActivity;

        modelApprovalActivity.typeProperties = {
            operationType: activity.approvalType === ApprovalType.Office365 ? OperationType.ApprovalMail : OperationType.ApprovalCustomHook,
        };

        switch (activity.approvalType) {
            case ApprovalType.Office365:
                modelApprovalActivity.typeProperties.operationType = OperationType.ApprovalMail;
                modelApprovalActivity.typeProperties.approvers = ValueAdapter.toResource(activity, FabricApprovalActivity.APPROVERS);
                modelApprovalActivity.typeProperties.approvalRequestTitle = ValueAdapter.toResource(activity, FabricApprovalActivity.APPROVAL_REQUEST_TITLE);
                if (!stringIsNullOrEmpty(activity.approvalDescription?.trim()) || !stringIsNullOrEmpty(FabricApprovalActivity.DESCRIPTION.templateValue(activity))) {
                    modelApprovalActivity.typeProperties.description = ValueAdapter.toResource(activity, FabricApprovalActivity.DESCRIPTION);
                }
                modelApprovalActivity.linkedServiceName = this._toLinkedServiceReferenceModel(activity.emailConnection, activity.linkedServiceParameters);
                if (!stringIsNullOrEmpty(activity.upn?.trim()) || !stringIsNullOrEmpty(FabricApprovalActivity.UPN.templateValue(activity))) {
                    modelApprovalActivity.typeProperties.upn = ValueAdapter.toResource(activity, FabricApprovalActivity.UPN);
                }
                break;

            case ApprovalType.CustomEndpoint:
                modelApprovalActivity.typeProperties.operationType = OperationType.ApprovalCustomHook;
                modelApprovalActivity.typeProperties.body = ValueAdapter.toResource(activity, FabricApprovalActivity.BODY);
                modelApprovalActivity.linkedServiceName = this._toLinkedServiceReferenceModel(activity.webConnection, activity.linkedServiceParameters);
                break;

            case ApprovalType.MicrosoftTeams:
                modelApprovalActivity.typeProperties.approvers = ValueAdapter.toResource(activity, FabricApprovalActivity.APPROVERS);
                modelApprovalActivity.typeProperties.approvalRequestTitle = ValueAdapter.toResource(activity, FabricApprovalActivity.APPROVAL_REQUEST_TITLE);
                if (!stringIsNullOrEmpty(activity.approvalDescription?.trim()) || !stringIsNullOrEmpty(FabricApprovalActivity.DESCRIPTION.templateValue(activity))) {
                    modelApprovalActivity.typeProperties.description = ValueAdapter.toResource(activity, FabricApprovalActivity.DESCRIPTION);
                }
                switch (activity.teamsOperationType) {
                    case PostInType.SendMessageInChannel:
                        modelApprovalActivity.typeProperties.operationType = OperationType.ApprovalChannelMessage;
                        modelApprovalActivity.typeProperties.teamId = ValueAdapter.toResource(activity, FabricApprovalActivity.TEAMID);
                        modelApprovalActivity.typeProperties.channelId = ValueAdapter.toResource(activity, FabricApprovalActivity.CHANNELID);
                        break;
                    case PostInType.SendMessageInGroupChat:
                        modelApprovalActivity.typeProperties.operationType = OperationType.ApprovalGroupChatMessage;
                        modelApprovalActivity.typeProperties.chatId = ValueAdapter.toResource(activity, FabricApprovalActivity.CHATID);
                        break;
                    default:
                }
                modelApprovalActivity.linkedServiceName = this._toLinkedServiceReferenceModel(activity.teamsConnection, activity.linkedServiceParameters);
                break;

            default:
                break;
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelApprovalActivity, parsed);
        }

        return modelApprovalActivity;
    }

    protected async _toModel(modelActivity: IFabricApprovalActivity): Promise<FabricApprovalActivity> {
        const activity: FabricApprovalActivity = FabricApprovalActivity.MODEL.create<FabricApprovalActivity>(modelActivity.name, modelActivity.description);
        this._updatedCommonProperties(modelActivity, activity);

        switch (modelActivity.typeProperties.operationType) {
            case OperationType.ApprovalMail:
                ValueAdapter.toModel(activity, FabricApprovalActivity.APPROVERS, modelActivity.typeProperties?.approvers);
                ValueAdapter.toModel(activity, FabricApprovalActivity.APPROVAL_REQUEST_TITLE, modelActivity.typeProperties?.approvalRequestTitle);
                ValueAdapter.toModel(activity, FabricApprovalActivity.DESCRIPTION, modelActivity.typeProperties?.description);
                activity.approvalType = ApprovalType.Office365;
                this._setEmailConnection(activity, modelActivity);
                ValueAdapter.toModel(activity, FabricApprovalActivity.UPN, modelActivity.typeProperties?.upn);
                break;

            case OperationType.ApprovalCustomHook:
                activity.approvalType = ApprovalType.CustomEndpoint;
                ValueAdapter.toModel(activity, FabricApprovalActivity.BODY, modelActivity.typeProperties?.body);
                this._setWebConnection(activity, modelActivity);
                break;

            case OperationType.ApprovalChannelMessage:
                activity.approvalType = ApprovalType.MicrosoftTeams;
                activity.teamsOperationType = PostInType.SendMessageInChannel;
                ValueAdapter.toModel(activity, FabricApprovalActivity.APPROVERS, modelActivity.typeProperties?.approvers);
                ValueAdapter.toModel(activity, FabricApprovalActivity.APPROVAL_REQUEST_TITLE, modelActivity.typeProperties?.approvalRequestTitle);
                ValueAdapter.toModel(activity, FabricApprovalActivity.TEAMID, modelActivity.typeProperties?.teamId);
                ValueAdapter.toModel(activity, FabricApprovalActivity.CHANNELID, modelActivity.typeProperties?.channelId);
                ValueAdapter.toModel(activity, FabricApprovalActivity.DESCRIPTION, modelActivity.typeProperties?.description);
                this._setTeamsConnection(activity, modelActivity);
                break;

            case OperationType.ApprovalGroupChatMessage:
                activity.approvalType = ApprovalType.MicrosoftTeams;
                activity.teamsOperationType = PostInType.SendMessageInGroupChat;
                ValueAdapter.toModel(activity, FabricApprovalActivity.APPROVERS, modelActivity.typeProperties?.approvers);
                ValueAdapter.toModel(activity, FabricApprovalActivity.APPROVAL_REQUEST_TITLE, modelActivity.typeProperties?.approvalRequestTitle);
                ValueAdapter.toModel(activity, FabricApprovalActivity.DESCRIPTION, modelActivity.typeProperties?.description);
                ValueAdapter.toModel(activity, FabricApprovalActivity.CHATID, modelActivity.typeProperties?.chatId);
                this._setTeamsConnection(activity, modelActivity);
                break;

            default:
                break;
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, FabricApprovalActivity.MODEL));
        return activity;
    }

    private _setEmailConnection(activity: FabricApprovalActivity, modelActivity: IFabricApprovalActivity) {
        if (modelActivity.linkedServiceName !== undefined) {
            activity.emailConnection = getFabricLsForParameterizedConnections(
                modelActivity.linkedServiceName,
                ModelConstants.office365EmailLinkedService,
                activity);

            if (!activity.emailConnection) {
                activity.emailConnection = ReferenceAdapter.toProxyModel<Office365EmailLinkedService>(Metamodel.modelFor(Office365EmailLinkedService),
                    modelActivity.linkedServiceName,
                    this.context);
            }
        }
    }

    private _setTeamsConnection(activity: FabricApprovalActivity, modelActivity: IFabricApprovalActivity) {
        if (modelActivity.linkedServiceName !== undefined) {
            activity.teamsConnection = getFabricLsForParameterizedConnections(
                modelActivity.linkedServiceName,
                ModelConstants.microsoftTeamsLinkedService,
                activity);

            if (!activity.teamsConnection) {
                activity.teamsConnection = ReferenceAdapter.toProxyModel<MicrosoftTeamsLinkedService>(Metamodel.modelFor(MicrosoftTeamsLinkedService),
                    modelActivity.linkedServiceName,
                    this.context);
            }
        }
    }

    private _setWebConnection(activity: FabricApprovalActivity, modelActivity: IFabricApprovalActivity) {
        if (modelActivity.linkedServiceName !== undefined) {
            activity.webConnection = getFabricLsForParameterizedConnections(
                modelActivity.linkedServiceName,
                ModelConstants.webActivityLinkedService,
                activity);

            if (!activity.webConnection) {
                activity.webConnection = ReferenceAdapter.toProxyModel<WebActivityLinkedService>(Metamodel.modelFor(WebActivityLinkedService),
                    modelActivity.linkedServiceName,
                    this.context);
            }
        }
    }
}
